import * as React from 'react'
import { useMemo } from 'react'

import { useNavigate, useSearch } from '@tanstack/react-router'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { Flex, Text } from '@chakra-ui/react'

import { Badge } from '@enechain/ecloud-designsystem'

import { Member, MemberStatus } from '~/gen/proto/bff/v1/model_pb'
import { useLoginOrganization } from '~/hooks/useLoginOrganization'
import MemberListFilter from '~/pages/Members/components/MemberListFilter'
import MembersTable from '~/pages/Members/components/MembersTable'
import { dict } from '~/pages/Members/dict'
import { useSelectableUserRoles } from '~/pages/Members/hooks/useSelectableUserRoles'
import { effectivePlans } from '~/pages/Members/utils/effectivePlans'
import {
  MemberFilterCondition,
  filterMembers,
  isMemberFilterActive,
} from '~/pages/Members/utils/filterMembers'
import {
  buildMemberCsv,
  downloadCsv,
  sanitizeFileName,
} from '~/pages/Members/utils/memberCsv'
import { toMemberCsvRow } from '~/pages/Members/utils/toMemberCsvRow'

type Props = {
  members: Member[]
}

// The filterable member list shown to organization admins. Members with the
// メンバー role cannot see the organization's plans/roles, so this section
// (filter + summary + CSV export) is admin-only.
export const FilteredMembers: React.FC<Props> = ({ members }) => {
  const { t, i18n } = useTranslation()
  const isEnglish = i18n.language === 'en'
  const organization = useLoginOrganization()
  const selectableUserRoles = useSelectableUserRoles()

  // The effective base contract plans define the filter's plan/role options
  // and the CSV plan columns.
  const plans = useMemo(
    () => effectivePlans(selectableUserRoles),
    [selectableUserRoles],
  )

  // Filter conditions live in the URL search params, so they survive
  // back-navigation from the member detail page and can be shared by URL.
  const search = useSearch({ from: '/_authenticated/members/' })
  const navigate = useNavigate()
  const condition: MemberFilterCondition = useMemo(() => {
    const planCodes = search.plan ?? []
    return {
      planCodes,
      // The role filter requires at least one selected plan (the role select
      // is disabled without one), so ignore role params that arrive without
      // a plan, e.g. via a hand-edited URL.
      roleNames: planCodes.length > 0 ? (search.role ?? []) : [],
      status:
        search.status !== undefined
          ? (search.status as MemberStatus)
          : undefined,
      keyword: search.keyword ?? '',
    }
  }, [search])
  const onChangeCondition = (next: MemberFilterCondition): void => {
    void navigate({
      to: '/members',
      search: {
        plan: next.planCodes.length > 0 ? next.planCodes : undefined,
        role: next.roleNames.length > 0 ? next.roleNames : undefined,
        status: next.status,
        keyword: next.keyword !== '' ? next.keyword : undefined,
      },
    })
  }

  const filteredMembers = useMemo(
    () => filterMembers(members, condition, plans),
    [members, condition, plans],
  )
  const isFiltering = isMemberFilterActive(condition)

  const onExportCsv = (): void => {
    const rows = filteredMembers.map((member) =>
      toMemberCsvRow(member, plans, isEnglish, dict),
    )
    const csv = buildMemberCsv(
      {
        name: t('pages.members.label.name'),
        email: t('pages.members.label.email'),
        organizationRole: t('pages.members.label.role'),
        status: t('pages.members.label.status'),
        createdAt: t('pages.members.label.createdAt'),
        phoneNumber: t('pages.members.label.phoneNumber'),
        mobileNumber: t('pages.members.label.mobileNumber'),
        planNames: plans.map((plan) =>
          isEnglish ? plan.engPlanName : plan.planName,
        ),
      },
      rows,
    )
    const baseName: string = t('pages.members.csv.fileName', {
      organizationName: sanitizeFileName(organization.name),
      date: format(new Date(), 'yyyy-MM-dd'),
    })
    downloadCsv(`${baseName}.csv`, csv)
  }

  return (
    <Flex flexDirection="column" gap="3">
      <MemberListFilter
        plans={plans}
        condition={condition}
        onChange={onChangeCondition}
        onExportCsv={onExportCsv}
        isExportDisabled={filteredMembers.length === 0}
      />
      <Flex alignItems="center" gap="2">
        <Text textStyle="headerMd">
          {t('pages.members.summary.count', {
            filtered: filteredMembers.length,
            total: members.length,
          })}
        </Text>
        {isFiltering && (
          <Badge status="info">{t('pages.members.filter.inProgress')}</Badge>
        )}
      </Flex>
      {filteredMembers.length === 0 ? (
        <Text color="Text.midEmphasis">
          {t('pages.members.filter.noMatch')}
        </Text>
      ) : (
        <MembersTable members={filteredMembers} />
      )}
    </Flex>
  )
}
